import type { PropsWithChildren } from "react";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { LoadingSpinner } from "widgets";

import { getSummaryPageTips } from "@/app-utils/help.js";
import { useURLViewID } from "@/app-utils/hooks/useUrl.js";
import type { Organism } from "@/graphql.js";
import type { ProjectColoredWidgetFragment } from "@/modules/projects/graphql/projects-queries.gql.ts";
import { loadView } from "@/redux/modules/saved-view.js";
import type { ReduxDispatch } from "@/redux/redux-utils.js";
import { ENTITY_TYPE } from "@/settings/settings.js";

import { SummaryButtonHeader } from "../core/navigation-panel/ButtonHeaderHoc.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import styleLayout from "../core/styles/PageLayout.module.scss";
import TipTour from "../core/TipTour.js";
import RefGenomeSelector from "./RefGenomeSelector.js";

interface IGenericViewOwnProps {
  title: string;
  subtitle?: string;
  entity: any;
  refGenomes?: Organism[]; // applies only to Variants Summary View
  headerTags?: JSX.Element[];
  project?: ProjectColoredWidgetFragment | null;
  headerAttributes?: JSX.Element[];
  isLoading?: boolean;
  getEntityName?: () => string;
}

const GenericView = (props: PropsWithChildren<IGenericViewOwnProps>) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const viewId = useURLViewID();

  useEffect(() => {
    if (viewId) {
      dispatch(loadView(viewId));
    }
  }, [dispatch, viewId]);

  return (
    <>
      <TipTour tips={getSummaryPageTips()}>
        <PageHeader
          title={props.title}
          subtitle={props.subtitle}
          tipTourId={"generic-page-title"}
          toolsWidgets={[
            props.entity?.groupType === ENTITY_TYPE.MUTATION ? (
              <RefGenomeSelector referenceGenomes={props.refGenomes} key={"generic-view-action-1"} />
            ) : null,
          ]}
          actionWidgets={[
            <SummaryButtonHeader
              entity={props.entity}
              getEntityName={props.getEntityName}
              key={"generic-view-action-2"}
            />,
          ]}
          tags={props.headerTags}
          widgets={props.headerAttributes}
          project={props.project}
          isLoading={props.isLoading}
        />
      </TipTour>
      <div className={styleLayout.page} style={{ padding: 7 }}>
        {props.isLoading ? <LoadingSpinner inCenter /> : props.children}
      </div>
    </>
  );
};

export default GenericView;
